<template>
  <div v-if="visible" class="fixed inset-0 bg-black/50 flex items-center justify-center z-[9999]">
    <div class="bg-white p-10 rounded-lg text-center w-[98%] max-w-[500px]">
      <p class="mb-2 text-[#303133]">{{ message }}</p>
      <el-progress :percentage="currentProgress" :status="currentProgress === 100 ? 'success' : ''" :stroke-width="8"
        indeterminate striped striped-flow class="mt-6" />
    </div>
  </div>
</template>

<script setup lang="ts">
  import { ref, reactive, computed } from 'vue';
  import {
    createFileProgressMap,
    updateFileProgress,
    calculateOverallProgress,
    createProgressHandler,
    type FileProgressInfo
  } from './uploadUtils';
  import { uploadObjectImages, uploadArrayFiles } from '@/api/modules/common';

  interface Props {
    message?: string;
  }

  const props = withDefaults(defineProps<Props>(), {
    message: '文件正在上传，请勿关闭页面'
  });

  const emit = defineEmits<{
    progress: [progress: number];
    complete: [];
  }>();

  const visible = ref(false);

  // 进度管理上下文
  const progressContext = reactive({
    fileProgressMap: new Map(),
    totalFileCount: 0,
    progress: 0
  });

  const currentProgress = computed(() => progressContext.progress);

  // 内部记录最近一次上传的文件列表
  const lastFiles = ref<(File | any)[]>([]);

  const initializeProgress = (files: (File | any)[]) => {
    lastFiles.value = files || [];
    progressContext.fileProgressMap = createFileProgressMap(lastFiles.value);
    progressContext.totalFileCount = lastFiles.value.length;
    progressContext.progress = 0;
  };

  const createHandler = () => {
    return createProgressHandler(
      progressContext.fileProgressMap,
      progressContext.totalFileCount,
      (newProgress: number) => {
        progressContext.progress = newProgress;
        emit('progress', newProgress);

        // 当所有文件都完成时，触发完成事件
        if (newProgress === 100) {
          emit('complete');
        }
      }
    );
  };

  interface UploadOptions {
    objectFields: string[];
    arrayFields?: string[];
    baseIndex?: number;
  }

  const extractFilesFromForm = (form: any, options: UploadOptions): (File | any)[] => {
    const files: (File | any)[] = [];
    options.objectFields?.forEach((key) => {
      const f = form?.[key];
      if (f) files.push(f);
    });
    options.arrayFields?.forEach((key) => {
      const arr = form?.[key];
      if (Array.isArray(arr)) {
        files.push(...arr);
      }
    });
    return files;
  };

  const progressHandler = ref<((progressEvent: any) => void) | null>(null);

  const open = () => { visible.value = true; };
  const close = () => { visible.value = false; };

  const progressMethods = {
    // 获取进度事件处理器，用于在上传函数中传入
    getProgressHandler: () => {
      return progressHandler.value;
    },

    // 标记所有文件为已完成（用于上传结束时的强制更新）
    markAllCompleted: () => {
      progressContext.fileProgressMap.forEach((_, key) => {
        progressContext.fileProgressMap.set(key, 100);
      });
      progressContext.progress = 100;
      emit('progress', 100);
      emit('complete');
      close();
    },
    resetProgress: () => {
      initializeProgress(lastFiles.value);
    },
    startUpload: async (form: any, options: UploadOptions) => {
      try {
        open();
        const files = extractFilesFromForm(form, options);
        initializeProgress(files);
        progressHandler.value = createHandler();

        const baseIdx = options.baseIndex ?? 0;
        let data = await uploadObjectImages(
          { ...form },
          baseIdx,
          options.objectFields || [],
          progressHandler.value
        );

        if (options.arrayFields && options.arrayFields.length) {
          for (const field of options.arrayFields) {
            data[field] = await uploadArrayFiles(data[field], baseIdx, progressHandler.value);
          }
        }

        progressMethods.markAllCompleted();
        return data;
      } catch (err) {
        close();
        throw err;
      }
    }
  };

  defineExpose(progressMethods);
</script>